import React from 'react';

/**
 * Downloads content from a given URI and initiates browser download
 * @param uri - The URI to download content from
 * @param fileName - Optional custom file name for the downloaded file
 * @returns Promise that resolves when download completes or rejects on error
 */
export const downloadFromUri = async (uri: string, fileName?: string): Promise<void> => {
  try {
    // Fetch the content from the URI
    const response = await fetch(uri);
    
    // Check if the request was successful
    if (!response.ok) {
      throw new Error(`Download failed: ${response.status} ${response.statusText}`);
    }
    
    // Get the blob from the response
    const blob = await response.blob();
    
    // Determine file name if not provided
    let downloadFileName = fileName;
    
    if (!downloadFileName) {
      // Try to get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      if (contentDisposition) {
        const filenameMatch = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (filenameMatch && filenameMatch[1]) {
          downloadFileName = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // If still no filename, extract it from the URI
      if (!downloadFileName) {
        const urlParts = uri.split('/');
        downloadFileName = urlParts[urlParts.length - 1].split('?')[0] || 'download';
      }
    }
    
    // Create a blob URL and trigger download
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = downloadFileName || 'download';
    
    // Append, click, and remove the link
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the blob URL
    URL.revokeObjectURL(blobUrl);
    
  } catch (error) {
    console.error('Download error:', error);
    throw error;
  }
};

// Optional React component for convenience
interface DownloadButtonProps {
  uri: string;
  fileName?: string;
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  onDownloadStart?: () => void;
  onDownloadComplete?: () => void;
  onDownloadError?: (error: Error) => void;
}

export const DownloadButton: React.FC<DownloadButtonProps> = ({
  uri,
  fileName,
  children = 'Download',
  className = '',
  disabled = false,
  onDownloadStart,
  onDownloadComplete,
  onDownloadError,
}) => {
  const handleClick = async () => {
    try {
      onDownloadStart?.();
      await downloadFromUri(uri, fileName);
      onDownloadComplete?.();
    } catch (error) {
      onDownloadError?.(error as Error);
    }
  };

  return (
    <button 
      className={className} 
      onClick={handleClick} 
      disabled={disabled}
    >
      {children}
    </button>
  );
};

export default downloadFromUri;